import { useMemo, useState } from 'react'

import {
  VStack,
  Box,
  Text,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import equal from 'fast-deep-equal'
import { Select } from 'src/components/Select/Select'
import { WarningMessage } from 'src/components/WarningMessage/WarningMessage'
import { errorMessages } from 'src/constants/errorMessages'
import { useModal } from 'src/hooks/use-modal'
import { ModalCancelButton } from 'src/lib/chakra-theme/components'
import {
  ExplanationReservationInviteeType,
  ExplanationType,
} from 'src/lib/gql-client'
import { assertNever } from 'src/utils/assertNever'

import { ScheduleInput } from '../ScheduleInput/ScheduleInput'

type ReservationFormValue = {
  reservedAt: string
  inviteeType: ExplanationReservationInviteeType
}

type Props = {
  isOpen: boolean
  formType: 'new' | 'edit'
  explanationType: ExplanationType
  isAgreementCompleted: boolean
  isPartnerMemberNotAssigned: boolean
  partnerHospitalName: string | undefined
  savedValue?: ReservationFormValue
  onSubmit: (value: ReservationFormValue) => Promise<void>
  onCancelReservation?: () => Promise<void>
  onClose: () => void
}

// 現在時刻+1時間の00分を返す 例) 現在時刻: 12:30 -> 13:00
const defaultPresetReservedAt = () =>
  dayjs()
    .set('hour', dayjs().get('hour') + 1)
    .set('minute', 0)
    .format()

export const EditReservationModal: React.FC<Props> = ({
  isOpen,
  formType,
  explanationType,
  isAgreementCompleted,
  isPartnerMemberNotAssigned,
  partnerHospitalName,
  savedValue,
  onSubmit,
  onCancelReservation,
  onClose: onCloseModal,
}) => {
  const modalTitle = formType === 'new' ? '次回面談の設定' : '面談予定変更'

  const selectableInviteeTypes: ExplanationReservationInviteeType[] =
    useMemo(() => {
      switch (explanationType) {
        case 'RemotePartner':
          return isAgreementCompleted
            ? ['PartnerMembers', 'Patient']
            : ['PartnerMembers']
        case 'Remote':
          return ['Patient']
        case 'InPerson':
          return isAgreementCompleted ? ['Patient'] : []
        case 'Invalid':
          return []
        default:
          return assertNever(explanationType)
      }
    }, [explanationType, isAgreementCompleted])

  const [value, setValue] = useState<ReservationFormValue>({
    reservedAt: savedValue?.reservedAt ?? defaultPresetReservedAt(),
    inviteeType: savedValue?.inviteeType ?? selectableInviteeTypes[0],
  })

  const { reservedAt, inviteeType } = value

  const getInviteeSelectLabel = (type: ExplanationReservationInviteeType) => {
    switch (type) {
      case 'Patient':
        return '患者のモバイル端末（患者宛に案内メールを送信）'
      case 'PartnerMembers':
        return `${partnerHospitalName}（担当者宛に案内メールを送信）`
      default:
        return assertNever(type)
    }
  }

  const { showModal, isOpen: isOpenCancelModal } = useModal()

  const isChanged = !equal(savedValue, value)

  const isBeforeNow = dayjs(reservedAt).isBefore(dayjs())

  return (
    <Modal
      isOpen={isOpen && !isOpenCancelModal}
      onClose={onCloseModal}
      size="4xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>{modalTitle}</ModalHeader>
        <ModalBody>
          <VStack spacing="8" align="start">
            {isAgreementCompleted && formType === 'new' && (
              <Text>
                同意説明は完了済みです。こちらの画面では説明完了後の個別面談を予約できます。
              </Text>
            )}
            <Box>
              <Text>面談の予定日時を設定してください。</Text>
              <Box mt="2">
                <ScheduleInput
                  value={reservedAt}
                  onChange={reservedAt => {
                    setValue({ ...value, reservedAt })
                  }}
                />
              </Box>
              {isBeforeNow && (
                <Text mt="2" color="red.500" fontSize="sm">
                  {errorMessages.afterNow()}
                </Text>
              )}
            </Box>
            <Box>
              <Text>患者がビデオ通話に参加される環境を指定してください。</Text>
              <Box mt="2">
                <Select
                  width={480}
                  isDisabled={
                    selectableInviteeTypes.length <= 1 ||
                    (formType === 'edit' && !reservedAt)
                  }
                  value={inviteeType}
                  items={selectableInviteeTypes.map(type => ({
                    label: getInviteeSelectLabel(type),
                    value: type,
                  }))}
                  onChange={inviteeType => {
                    setValue({ ...value, inviteeType })
                  }}
                />
              </Box>
            </Box>
            {inviteeType === 'PartnerMembers' && isPartnerMemberNotAssigned && (
              <WarningMessage message="パートナー施設の担当者が選択されていないため案内メールは送信されません。" />
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <ModalCancelButton />
          <Button
            isDisabled={
              (formType === 'new' && !reservedAt) ||
              (formType === 'edit' && !isChanged) ||
              isBeforeNow
            }
            onClick={async () => {
              if (formType === 'edit' && !reservedAt) {
                showModal({
                  title: '面談予約を取り消しますか？',
                  content: '取り消し時には案内先へ取消のメールが送信されます。',
                  submitText: '取り消す',
                  size: 'xl',
                  submitButtonColor: 'red',
                  onSubmit: async () => {
                    await onCancelReservation?.()
                  },
                })
                return
              }
              await onSubmit(value)
            }}
          >
            設定する
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
